import { FORMAT_DATE_TO_DAYJS, TIME_FORMAT_DAYJS } from "@/constants/dateTime";
import {
  AMPM_REGEX,
  DAY_REGEX,
  MONTH_REGEX,
  SECOND_REGEX,
  TIME_REGEX,
  YEAR_REGEX
} from "@/constants/regex";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { DataSchemaType } from "./date";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
export { DataSchemaType } from "../types/dataset";

// Array of possible date formats
const dateFormats = [
  "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO with milliseconds and timezone
  "YYYY-MM-DDTHH:mm:ssZ", // ISO without milliseconds
  "YYYY-MM-DD HH:mm:ss",
  "DD-MM-YYYY HH:mm",
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "MM/DD/YYYY hh:mm A",
  "DD MMMM YYYY",
  "hh:mm:ss A",
  "hh:mm A",
  "HH:mm:ss",
  "HH:mm"
];
/**
 * Parses a date string using dayjs with multiple format fallbacks.
 *
 * This function attempts to parse the input string in multiple ways:
 * 1. First tries dayjs's native ISO 8601 parsing
 * 2. If that fails, tries each format in the predefined dateFormats array
 *
 * @param dateString - The date string to parse (can be any type, but only strings are processed)
 * @returns A dayjs.Dayjs object in UTC if parsing succeeds, null otherwise
 */
export function parseWithDayjs(
  dateString: string | unknown
): dayjs.Dayjs | null {
  if (typeof dateString !== "string" || !dateString) {
    return null;
  }

  // 1. Try parsing with dayjs's native ISO 8601 support first
  const nativeParsedDate = dayjs.utc(dateString);
  if (nativeParsedDate.isValid()) {
    return nativeParsedDate;
  }

  // 2. If native parsing fails, try our list of custom formats
  for (const format of dateFormats) {
    const date = dayjs.utc(dateString, format, true); // Use strict parsing
    if (date.isValid()) return date;
  }

  return null;
}

/**
 * Checks if a date string contains seconds
 * @param dateString - The date/time string to check
 * @returns true if the string contains seconds (HH:MM:SS format)
 */
export const hasSeconds = (dateString: string): boolean => {
  // Check if the time part has seconds (HH:MM:SS vs HH:MM)
  const timeMatch = /\d{2}:\d{2}(:\d{2})?/.exec(dateString);
  return timeMatch ? timeMatch[1] !== undefined : false;
};

/**
 *
 * @param format - "datetime" | "date" | "time". Default is "datetime".
 * @param timeZone - IANA timezone string (e.g., "Asia/Jakarta"). Default is "UTC".
 * @returns Formatted string or original input if invalid.
 *
 */
export function formatDate(
  date: string,
  format: DataSchemaType.DATE | DataSchemaType.DATE_TIME | DataSchemaType.TIME,
  timeZone?: { code: string; name: string }
): string {
  if (typeof date !== "string" || !date.trim()) return "";
  if (format === "time") return date;

  // Check if the input date string includes timezone information (e.g., "Z", "+hh:mm", or "-hh:mm").
  const hasTimezone = /([Zz]|[+-]\d{2}:\d{2})$/.test(date);

  if (hasTimezone) {
    const parsed = dayjs.utc(date);
    const timezone = parsed.tz(timeZone?.code);

    if (format === DataSchemaType.DATE) {
      return timezone.format("YYYY-MM-DD");
    } else if (format === DataSchemaType.DATE_TIME) {
      return timezone.format("YYYY-MM-DD HH:mm:ss");
    }

    if (hasSeconds(date)) {
      return timezone.format("HH:mm:ss");
    }
    return timezone.format("HH:mm");
  }

  return date;
}

export function formatToTimezone(
  date: string,
  timeZone?: { code: string; name: string }
) {
  if (typeof date !== "string" || !date.trim()) return "";
  if (timeZone) {
    const parsed = dayjs.utc(date);
    const timezone = parsed.tz(timeZone?.code);
    return timezone.format();
  }
  return date;
}

/**
 * Special utils to convert time_format from Go backend to Day.js format in Dataset Detail
 * @param date string date
 * @param goFormat - Format string from backend (Go)
 * @param data_type - Optional data type to handle default formats
 * @returns Converted Day.js format
 */
export function convertFormatterDayjs(
  date: string,
  goFormat: string,
  data_type?: DataSchemaType
): string {
  let format: string;

  if (!goFormat && data_type === DataSchemaType.DATE) format = "YYYY-MM-DD";
  else if (!goFormat && data_type === DataSchemaType.DATE_TIME) format = "";
  else if (!goFormat && data_type === DataSchemaType.TIME) return date;
  else {
    const mapped = FORMAT_DATE_TO_DAYJS[goFormat];
    format =
      mapped ||
      goFormat
        .replace(TIME_REGEX, "HH:mm")
        .replace(SECOND_REGEX, ":ss")
        .replace(AMPM_REGEX, "A")
        .replace(MONTH_REGEX, "MMMM")
        .replace(YEAR_REGEX, "YYYY")
        .replace(DAY_REGEX, "DD");
  }

  // Handle time only format
  if (TIME_FORMAT_DAYJS.includes(format)) {
    const parsedTime = dayjs(date, TIME_FORMAT_DAYJS);
    return parsedTime.format(format);
  }

  return dayjs.utc(date).format(format);
}
